import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import * as userService from 'services/user';
import * as skillV2Service from 'services/skillV2';
import * as skillService from 'services/skillService';
import type { GlobalRatingPayload } from 'services/skillV2';

import { handleError } from 'utils/errorHandler';
import { getFlattenSkillTree } from 'utils/skillV2';
import type { EndLevelSkillType } from 'utils/reactSelect';

import type { SkillNode } from 'interface/skillTree';
import { GlobalRatingItem, SelectedSkillItem } from 'interface/skill';

import { SKILL_QUERY_KEYS } from './keys';

type UpdateUserSkillsPayload = {
  employeeId: number | string;
  skills: Array<{ id: number | string; rating: number }>;
};

type GlobalRatingResponse = {
  data?: {
    globalRatings?: GlobalRatingItem[];
  };
};

const EMPTY_GLOBAL_RATINGS: GlobalRatingItem[] = [];
const EMPTY_SELECTED_SKILLS: SelectedSkillItem[] = [];

const formatSelectedSkills = (skills: SelectedSkillItem[]): SelectedSkillItem[] => {
  return skills.map((skill) => ({ id: skill.id, rating: skill.rating, skillStatus: skill.skillStatus }));
};

export const GLOBAL_RATINGS_QUERY_KEY = [SKILL_QUERY_KEYS.GLOBAL_RATINGS] as const;

export const ALL_SKILLS_QUERY_KEY = [SKILL_QUERY_KEYS.ALL_SKILLS] as const;

export const getUserSelectedSkillsQueryKey = (employeeId?: number | string) =>
  [SKILL_QUERY_KEYS.SELECTED_SKILLS, employeeId] as const;

export const useGlobalRatingsQuery = (): UseQueryResult<GlobalRatingItem[], Error> => {
  return useQuery<GlobalRatingResponse, Error, GlobalRatingItem[]>({
    queryKey: GLOBAL_RATINGS_QUERY_KEY,
    queryFn: () => skillV2Service.fetchGlobalRating() as Promise<GlobalRatingResponse>,
    select: (response) => response.data?.globalRatings ?? EMPTY_GLOBAL_RATINGS,
    staleTime: Infinity,
  });
};

export const useAllSkillsQuery = (triggerSearchSkillTree?: number): UseQueryResult<EndLevelSkillType[], Error> => {
  return useQuery<{ data: SkillNode[] }, Error, EndLevelSkillType[]>({
    queryKey: [...ALL_SKILLS_QUERY_KEY, triggerSearchSkillTree],
    queryFn: () => skillV2Service.fetchAllSkills() as Promise<{ data: SkillNode[] }>,
    select: (response) => (Array.isArray(response.data) ? getFlattenSkillTree(response.data) : []),
  });
};

export const useUpdateGlobalRatingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, GlobalRatingPayload>({
    mutationFn: async (payload) => {
      await skillV2Service.updateGlobalRating(payload);
    },
    onError: (error) => {
      handleError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GLOBAL_RATINGS_QUERY_KEY });
    },
  });
};

export const useUserSelectedSkillsQuery = (
  employeeId?: number | string,
  enabled = true
): UseQueryResult<SelectedSkillItem[], Error> => {
  return useQuery<{ data?: SelectedSkillItem[] }, Error, SelectedSkillItem[]>({
    queryKey: getUserSelectedSkillsQueryKey(employeeId),
    queryFn: () => skillService.fetchByUserId(employeeId ?? ''),
    select: (response) =>
      Array.isArray(response.data) ? formatSelectedSkills(response.data as SelectedSkillItem[]) : EMPTY_SELECTED_SKILLS,
    enabled: Boolean(employeeId) && enabled,
  });
};

export const useUpdateUserSelectedSkillsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateUserSkillsPayload>({
    mutationFn: async ({ employeeId, skills }) => {
      await userService.updateSkillsV2(employeeId, { skills });
    },
    onError: (error) => {
      handleError(error);
    },
    onSuccess: (_data, { employeeId, skills }) => {
      queryClient.setQueryData(getUserSelectedSkillsQueryKey(employeeId), skills);
      queryClient.invalidateQueries({ queryKey: getUserSelectedSkillsQueryKey(employeeId) });
    },
  });
};
